import { Box } from '@mui/material'
import { PaginationState, Table } from '@tanstack/table-core'
import React, { useMemo } from 'react'

interface PaginationProps {
  table: Table<any>
  onPageChange?: (state: PaginationState) => void
  paginationState: PaginationState
  totalRows: number
}

const Pagination = ({
  table,
  totalRows,
  onPageChange,
  paginationState,
}: PaginationProps) => {
  const { pageIndex, pageSize } = paginationState

  const renderPaginationDescription = useMemo(() => {
    const fromItem =
      pageSize * pageIndex + 1 < totalRows ? pageSize * pageIndex + 1 : 0
    const toItem =
      (pageIndex + 1) * pageSize < totalRows
        ? (pageIndex + 1) * pageSize
        : totalRows
    return (
      <Box display="flex" gap={1} alignItems="center">
        From <strong> {fromItem}</strong>
        {'-'}
        <strong>{toItem} </strong> items of <strong> {totalRows}</strong>
        items
      </Box>
    )
  }, [pageIndex, pageSize, totalRows])

  return (
    <Box justifyContent="flex-end" display="flex" alignItems="center" gap={2}>
      <button
        className="border rounded p-1"
        onClick={() => {
          if (onPageChange) {
            onPageChange({
              ...paginationState,
              pageIndex: 0,
            })
          }
          table.setPageIndex(0)
        }}
        disabled={!table.getCanPreviousPage()}
      >
        {'<<'}
      </button>
      <button
        className="border rounded p-1"
        onClick={() => {
          if (onPageChange) {
            onPageChange({
              ...paginationState,
              pageIndex: pageIndex - 1,
            })
          }
          table.previousPage()
        }}
        disabled={!table.getCanPreviousPage()}
      >
        {'<'}
      </button>
      <button
        className="border rounded p-1"
        onClick={() => {
          if (onPageChange) {
            onPageChange({
              ...paginationState,
              pageIndex: pageIndex + 1,
            })
          }
          table.nextPage()
        }}
        disabled={!table.getCanNextPage()}
      >
        {'>'}
      </button>
      <button
        className="border rounded p-1"
        onClick={() => {
          const lastPage = table.getPageCount() - 1
          table.setPageIndex(lastPage)
          if (onPageChange) {
            onPageChange({
              ...paginationState,
              pageIndex: lastPage,
            })
          }
        }}
        disabled={!table.getCanNextPage()}
      >
        {'>>'}
      </button>
      {renderPaginationDescription}

      <select
        value={table.getState().pagination.pageSize}
        onChange={e => {
          if (onPageChange) {
            onPageChange({
              ...paginationState,
              pageSize: Number(e.target.value),
            })
          }
          table.setPageSize(Number(e.target.value))
        }}
      >
        {[10, 20, 30, 40, 50].map(pageSize => (
          <option key={pageSize} value={pageSize}>
            Show {pageSize}
          </option>
        ))}
      </select>
    </Box>
  )
}

export default Pagination
